import { useEffect } from 'react';
import { api } from '../api/client';

export type Publicidad = {
  id: number;
  titulo: string;
  tipo: 'popup' | 'banner' | 'patrocinado';
  color_fondo: string | null;
  imagen_url: string | null;
  cta_texto: string | null;
  cta_url: string | null;
  cta_tipo: 'link' | 'whatsapp' | 'telefono' | null;
};

function registrarImpresion(id: number) {
  api(`/publicidad?action=impresion`, { method: 'POST', body: JSON.stringify({ id }) }).catch(() => {});
}

function registrarClic(id: number) {
  api(`/publicidad?action=clic`, { method: 'POST', body: JSON.stringify({ id }) }).catch(() => {});
}

function abrirCta(p: Publicidad) {
  if (!p.cta_url) return;
  registrarClic(p.id);
  if (p.cta_tipo === 'telefono') {
    window.location.href = `tel:${p.cta_url.replace(/\D/g, '')}`;
  } else if (p.cta_tipo === 'whatsapp') {
    window.open(`https://wa.me/${p.cta_url.replace(/\D/g, '')}`);
  } else {
    window.open(p.cta_url, '_blank', 'noopener');
  }
}

// ── Popup modal ──────────────────────────────────────────────────
type PopupProps = {
  pub: Publicidad;
  onClose: () => void;
};

export function PublicidadPopup({ pub, onClose }: PopupProps) {
  const bg = pub.color_fondo || '#ffffff';

  useEffect(() => {
    registrarImpresion(pub.id);
    sessionStorage.setItem(`pub_seen_${pub.id}`, '1');
  }, [pub.id]);

  return (
    <>
      <div
        className="fixed inset-0 z-50"
        style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
        onClick={onClose}
      />
      <div
        className="fixed inset-x-4 z-50 rounded-2xl shadow-2xl overflow-hidden"
        style={{ top: '50%', transform: 'translateY(-50%)', maxWidth: 360, margin: '0 auto', backgroundColor: bg }}
      >
        {/* Botón cerrar */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold"
          style={{ backgroundColor: 'rgba(0,0,0,0.18)', color: '#fff' }}
          aria-label="Cerrar"
        >
          ✕
        </button>

        {pub.imagen_url && (
          <img
            src={pub.imagen_url}
            alt={pub.titulo}
            className="w-full object-cover"
            style={{ maxHeight: 200 }}
          />
        )}

        <div className="px-5 pt-4 pb-5 space-y-3">
          <p className="font-extrabold text-base leading-tight" style={{ color: '#1a1a1a' }}>
            {pub.titulo}
          </p>

          {pub.cta_texto && pub.cta_url && (
            <button
              onClick={() => abrirCta(pub)}
              className="w-full rounded-xl py-3 font-bold text-sm text-white"
              style={{ backgroundColor: '#32329f' }}
            >
              {pub.cta_texto}
            </button>
          )}

          <button
            onClick={onClose}
            className="w-full border border-app rounded-xl py-2.5 text-sm text-gray-500 font-medium"
          >
            Cerrar
          </button>
        </div>
      </div>
    </>
  );
}

// ── Cards inline (banner / patrocinado) ─────────────────────────
type CardProps = {
  pub: Publicidad;
};

export function PublicidadCard({ pub }: CardProps) {
  const bg = pub.color_fondo || '#ffffff';
  const hasCta = pub.cta_texto && pub.cta_url;

  useEffect(() => {
    registrarImpresion(pub.id);
  }, [pub.id]);

  return (
    <div
      className="rounded-2xl overflow-hidden shadow-sm"
      style={{ backgroundColor: bg, border: '1px solid rgba(0,0,0,0.08)' }}
    >
      {pub.imagen_url && (
        <img
          src={pub.imagen_url}
          alt={pub.titulo}
          className="w-full object-cover"
          style={{ maxHeight: 160, display: 'block' }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      )}
      <div className="px-4 py-3 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: '#9ca3af' }}>
            Publicidad
          </p>
          <p className="font-bold text-sm leading-snug" style={{ color: '#1a1a1a' }}>{pub.titulo}</p>
        </div>
        {hasCta && (
          <button
            onClick={() => abrirCta(pub)}
            className="flex-shrink-0 rounded-xl px-4 py-2 text-xs font-bold text-white"
            style={{ backgroundColor: '#32329f', whiteSpace: 'nowrap' }}
          >
            {pub.cta_texto}
          </button>
        )}
      </div>
    </div>
  );
}
